import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Search, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_PILL = {
  excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  good:      'bg-blue-100 text-blue-800 border-blue-200',
  standard:  'bg-amber-100 text-amber-800 border-amber-200',
  needs_improvement: 'bg-rose-100 text-rose-800 border-rose-200',
};

const StandardsReview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('any');
  const [test, setTest] = useState('any');
  const [gender, setGender] = useState('any');
  const [ageBand, setAgeBand] = useState('any');
  const [direction, setDirection] = useState('any');

  useEffect(() => {
    if (user?.role !== 'Admin') {
      navigate('/dashboard');
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/assessments/standards`);
        setRows(res.data?.standards || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load standards');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  // Pivot from "flat tier rows" → one row per (test, gender, age_band)
  // with all 4 tiers as columns.
  const pivoted = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      const key = `${r.test_type}|${r.gender}|${r.age_band}`;
      if (!m.has(key)) {
        m.set(key, {
          test_type: r.test_type,
          test_category: r.test_category,
          gender: r.gender,
          age_band: r.age_band,
          unit: r.unit,
          direction: r.direction,
          tiers: {},
        });
      }
      m.get(key).tiers[r.ui_key] = r.threshold_summary;
    });
    return Array.from(m.values()).sort((a, b) => {
      // Sort by category → test → gender → age band.
      const ca = (a.test_category || '').localeCompare(b.test_category || '');
      if (ca) return ca;
      const ta = a.test_type.localeCompare(b.test_type);
      if (ta) return ta;
      const ga = a.gender.localeCompare(b.gender);
      if (ga) return ga;
      return a.age_band.localeCompare(b.age_band);
    });
  }, [rows]);

  const facets = useMemo(() => {
    const cat = new Set(), tst = new Set(), gen = new Set(), ab = new Set(), dir = new Set();
    pivoted.forEach(r => {
      if (r.test_category) cat.add(r.test_category);
      if (r.test_type) tst.add(r.test_type);
      if (r.gender) gen.add(r.gender);
      if (r.age_band) ab.add(r.age_band);
      if (r.direction) dir.add(r.direction);
    });
    const sortAgeBand = (xs) => xs.sort((a, b) => Number(a.split('-')[0]) - Number(b.split('-')[0]));
    return {
      categories: Array.from(cat).sort(),
      tests: Array.from(tst).sort(),
      genders: Array.from(gen).sort(),
      age_bands: sortAgeBand(Array.from(ab)),
      directions: Array.from(dir).sort(),
    };
  }, [pivoted]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pivoted.filter(r => {
      if (category !== 'any' && r.test_category !== category) return false;
      if (test !== 'any' && r.test_type !== test) return false;
      if (gender !== 'any' && r.gender !== gender) return false;
      if (ageBand !== 'any' && r.age_band !== ageBand) return false;
      if (direction !== 'any' && r.direction !== direction) return false;
      if (q) {
        const haystack = [
          r.test_type, r.test_category, r.gender, r.age_band,
          r.tiers.excellent, r.tiers.good, r.tiers.standard, r.tiers.needs_improvement,
        ].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [pivoted, search, category, test, gender, ageBand, direction]);

  if (user?.role !== 'Admin') return null;

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="standards-review">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
              Standards Review
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1 max-w-2xl text-sm">
              Read-only reference of the current Phase 2 standards engine.
              Source-of-truth: <code>Standards 2.docx</code>. Use it to verify
              the thresholds the auto-classifier is applying.
            </p>
          </div>
          <Badge variant="outline" className="text-[11px] flex items-center gap-1">
            <Lock className="w-3 h-3" /> Read-only · Admin
          </Badge>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="pt-5 grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2">
              <p className="text-xs text-gray-500 mb-1">Search</p>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                <Input
                  className="pl-8"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Test, age band, threshold…"
                  data-testid="std-search"
                />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Group</p>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="std-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All groups</SelectItem>
                  {facets.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Test</p>
              <Select value={test} onValueChange={setTest}>
                <SelectTrigger data-testid="std-filter-test"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All tests</SelectItem>
                  {facets.tests.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Gender</p>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger data-testid="std-filter-gender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All</SelectItem>
                  {facets.genders.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Age band</p>
              <Select value={ageBand} onValueChange={setAgeBand}>
                <SelectTrigger data-testid="std-filter-age-band"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All</SelectItem>
                  {facets.age_bands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Direction</p>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="std-filter-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All</SelectItem>
                  {facets.directions.map(d => (
                    <SelectItem key={d} value={d}>
                      {d === 'higher_is_better' ? 'Higher is better' : 'Lower is better'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Standards <span className="text-xs text-gray-500 font-normal">({filtered.length} rows)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm py-6 text-center"
                 data-testid="std-empty">
                No standards match these filters.
              </p>
            ) : (
              <table className="w-full text-sm border-collapse min-w-[900px]"
                     data-testid="std-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                    <th className="py-2 pr-3">Group</th>
                    <th className="py-2 pr-3">Test</th>
                    <th className="py-2 pr-3">Gender</th>
                    <th className="py-2 pr-3">Age</th>
                    <th className="py-2 pr-3">Unit</th>
                    <th className="py-2 pr-3">Dir.</th>
                    <th className="py-2 pr-3">Excellent</th>
                    <th className="py-2 pr-3">Good</th>
                    <th className="py-2 pr-3">Standard</th>
                    <th className="py-2 pr-3">Needs Improvement</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={`${r.test_type}-${r.gender}-${r.age_band}-${i}`}
                        className="border-b last:border-0 dark:border-gray-800"
                        data-testid={`std-row-${r.test_type}-${r.gender}-${r.age_band}`}>
                      <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">{r.test_category}</td>
                      <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">{r.test_type}</td>
                      <td className="py-2 pr-3">{r.gender}</td>
                      <td className="py-2 pr-3">{r.age_band}</td>
                      <td className="py-2 pr-3 text-gray-500">{r.unit}</td>
                      <td className="py-2 pr-3 text-gray-500 text-[11px]">
                        {r.direction === 'higher_is_better' ? '↑ higher' : '↓ lower'}
                      </td>
                      {['excellent', 'good', 'standard', 'needs_improvement'].map(k => (
                        <td key={k} className="py-2 pr-3">
                          <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${TIER_PILL[k]}`}>
                            {r.tiers[k] || '—'}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default StandardsReview;
